import { useFormik } from "formik";

import {
  Accordion,
  AccordionVariant,
} from "@vega-core/components/Accordion/Accordion";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { Switch } from "@vega-core/components/Switch/Switch";
import { m } from "@vega-core/paraglide/messages";

import { useLpVisibility } from "../../hooks/useLpVisibility";
import { ReportSectionMessage } from "../../pages/components/ReportSectionMessage";

type LpVisibilitySectionProps = {
  limitedPartnerId: string;
};

export const LpVisibilitySection = ({
  limitedPartnerId,
}: LpVisibilitySectionProps) => {
  const { fields, isLoading, isError, refetch, save, isSaving } =
    useLpVisibility(limitedPartnerId);

  const formik = useFormik({
    initialValues: { fields },
    enableReinitialize: true,
    onSubmit: (values) => save(values.fields),
  });

  const allVisible =
    formik.values.fields.length > 0 &&
    formik.values.fields.every((field) => field.isVisible);

  const toggleAll = () =>
    formik.setValues({
      fields: formik.values.fields.map((field) => ({
        ...field,
        isVisible: !allVisible,
      })),
    });

  const renderBody = () => {
    if (isError) {
      return (
        <div className="px-4 pb-4">
          <ReportSectionMessage
            dataTestId="lp-visibility-error"
            message={m.common_error_somethingWentWrong()}
            onRetry={refetch}
          />
        </div>
      );
    }

    return (
      <form
        className="px-4 pb-4"
        data-testid="lp-visibility-form"
        onSubmit={formik.handleSubmit}
      >
        <div className="flex justify-end pb-1">
          <ThemedButton
            dataTestId="lp-visibility-toggle-all"
            disabled={isSaving || formik.values.fields.length === 0}
            onClick={toggleAll}
            type="button"
            variant="text"
          >
            {allVisible
              ? m.coInvestments_insights_lpVisibility_hideAll()
              : m.coInvestments_insights_lpVisibility_showAll()}
          </ThemedButton>
        </div>
        <div className="grid grid-cols-1 gap-x-12 gap-y-1 md:grid-cols-2">
          {formik.values.fields.map((field, index) => (
            <div
              className="flex items-center justify-between py-2"
              key={field.key}
            >
              <span className="body-base-regular text-text/primary">
                {field.label}
              </span>
              <Switch
                checked={field.isVisible}
                dataTestId={`lp-visibility-toggle-${field.key}`}
                onChange={(checked) =>
                  formik.setFieldValue(`fields.${index}.isVisible`, checked)
                }
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end pt-4">
          <ThemedButton
            dataTestId="lp-visibility-save"
            disabled={!formik.dirty || isSaving}
            isLoading={isSaving}
            size="default"
            type="submit"
            variant="primary"
          >
            {m.common_save()}
          </ThemedButton>
        </div>
      </form>
    );
  };

  return (
    <div data-testid="lp-visibility-section">
      <Accordion
        collapsible
        isLoading={isLoading}
        items={[
          {
            value: "lp-visibility",
            title: (
              <div className="flex flex-col text-left">
                <span className="body-semi-large-semibold text-text/primary">
                  {m.coInvestments_insights_lpVisibility_title()}
                </span>
                <span className="body-base-regular text-text/secondary">
                  {m.coInvestments_insights_lpVisibility_subtitle()}
                </span>
              </div>
            ),
            content: renderBody(),
          },
        ]}
        type="single"
        variant={AccordionVariant.FAQ}
      />
    </div>
  );
};
